import React from 'react';
import { useNotification } from '../../contexts/NotificationContext';

const NotificationContainer = () => {
    const { notifications, removeNotification } = useNotification();

    if (notifications.length === 0) return null;

    return (
        <div style={styles.container}>
            {notifications.map((notification) => (
                <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRemove={removeNotification}
                />
            ))}
        </div>
    );
};

const NotificationItem = ({ notification, onRemove }) => {
    const getIcon = (type) => {
        switch (type) {
            case 'success': return '✅';
            case 'error': return '❌';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            default: return 'ℹ️';
        }
    };

    const getTypeStyles = (type) => {
        switch (type) {
            case 'success':
                return {
                    backgroundColor: 'rgba(76, 175, 80, 0.15)',
                    borderColor: '#4CAF50',
                    color: '#4CAF50'
                };
            case 'error':
                return {
                    backgroundColor: 'rgba(244, 67, 54, 0.15)',
                    borderColor: '#F44336',
                    color: '#F44336'
                };
            case 'warning':
                return {
                    backgroundColor: 'rgba(255, 152, 0, 0.15)',
                    borderColor: '#FF9800',
                    color: '#FF9800'
                };
            case 'info':
            default:
                return {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                    borderColor: '#2196F3',
                    color: '#2196F3'
                };
        }
    };

    const typeStyles = getTypeStyles(notification.type);

    return (
        <div 
            style={{
                ...styles.notification,
                ...typeStyles
            }}
            onClick={() => onRemove(notification.id)}
        >
            <div style={styles.content}>
                <span style={styles.icon}>{getIcon(notification.type)}</span>
                <span style={styles.message}>{notification.message}</span>
            </div>
            <button 
                style={styles.closeButton}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(notification.id);
                }}
            >
                ×
            </button>
        </div>
    );
};

const styles = {
    container: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '400px',
        width: '100%'
    },
    notification: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderRadius: '12px',
        border: '2px solid',
        backdropFilter: 'blur(8px)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        animation: 'slideInRight 0.3s ease-out',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        minHeight: '60px'
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1
    },
    icon: {
        fontSize: '20px',
        flexShrink: 0
    },
    message: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#f8f1e4',
        lineHeight: '1.4',
        flex: 1
    },
    closeButton: {
        background: 'none',
        border: 'none',
        color: '#f8f1e4',
        fontSize: '24px',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        opacity: 0.7,
        transition: 'all 0.2s ease',
        marginLeft: '12px',
        flexShrink: 0
    }
};

// Inject CSS animations
if (!document.querySelector('#notification-animations')) {
    const style = document.createElement('style');
    style.id = 'notification-animations';
    style.textContent = `
        @keyframes slideInRight {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        
        .notification-close-hover:hover {
            opacity: 1;
            background-color: rgba(255, 255, 255, 0.1);
        }
        
        .notification-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
        }
        
        @media (max-width: 480px) {
            .notification-container {
                top: 10px;
                right: 10px;
                left: 10px;
                max-width: none;
            }
        }
    `;
    document.head.appendChild(style);
}

export default NotificationContainer;
